import { useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight } from "lucide-react";
import Particles from "../components/Particles";
import Marquee from "../components/Marquee";
import SectionHeading from "../components/SectionHeading";
import ServicesGrid from "../components/sections/ServicesGrid";
import ReputationSpotlight from "../components/sections/ReputationSpotlight";
import ClientReviews from "../components/sections/ClientReviews";
import WhyGrid from "../components/sections/WhyGrid";
import ProcessTimeline from "../components/sections/ProcessTimeline";
import FAQAccordion from "../components/sections/FAQAccordion";
import { Reveal } from "../components/Reveal";
import { useConsultation } from "../context/ConsultationContext";
import { whatsappLink, BRAND } from "../data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.12 } }),
};

export default function Home() {
  const { openModal } = useConsultation();

  useEffect(() => {
    document.title = "JOBLSX — Professional Digital Consulting & Account Management";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="orb h-[420px] w-[420px] bg-blue-600/25 left-[-100px] top-[10%]" />
        <div className="orb h-[360px] w-[360px] bg-blue-500/15 right-[-80px] bottom-[5%]" style={{ animationDelay: "3s" }} />
        <Particles count={30} />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-slate-300"
          >
            <span className="h-2 w-2 rounded-full bg-blue-500" /> {BRAND.tagline}
          </motion.div>

          <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-tighter sm:text-6xl lg:text-8xl">
            {["Grow Smarter.", "Manage Better."].map((t, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {i === 1 ? (
                    <span className="text-blue-500">{t}</span>
                  ) : (
                    t
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl"
          >
            JOBLSX provides professional account management, digital consulting, profile optimization,
            technical issue guidance, and growth strategy for creators, gamers, businesses, and professionals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <button
              onClick={openModal}
              className="flex items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-4 font-medium text-white shadow-[0_0_24px_rgba(37,99,235,0.45)] transition-all duration-300 hover:bg-blue-700 hover:shadow-[0_0_36px_rgba(37,99,235,0.7)] hover:-translate-y-1"
              data-testid="hero-book-button"
            >
              Book Consultation <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 font-medium text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
              data-testid="hero-whatsapp-button"
            >
              <MessageCircle className="h-5 w-5" /> Contact on WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      <Marquee items={["Strategy", "Growth", "Audit", "Management", "Support", "Consulting"]} />

      {/* ABOUT */}
      <section className="relative py-24 md:py-32" id="about">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading overline="Who We Are" title="A digital consulting partner built around your goals." />
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg leading-relaxed text-slate-400">
                  JOBLSX is a digital consulting company specializing in online account management, growth strategy,
                  technical troubleshooting, profile optimization, and personalized consulting. We help clients improve
                  performance, understand platform features, and optimize their digital presence.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden rounded-3xl border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Modern consulting workspace"
                  className="h-[420px] w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-24 md:py-32" id="services">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="What We Do" title="Services crafted for measurable clarity." className="mb-14" />
          <ServicesGrid />
        </div>
      </section>

      {/* CLIENT REVIEWS — carousel below Services */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a]" id="reviews">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Client Reviews" title="What clients say about JOBLSX." className="mb-12" />
          <ClientReviews />
        </div>
      </section>

      {/* REPUTATION MANAGEMENT — MICRO-CELEBRITY SPOTLIGHT */}
      <section className="relative py-24 md:py-32" id="reputation">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <ReputationSpotlight />
        </div>
      </section>

      {/* WHY */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a]">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Why Choose JOBLSX" title="Principles our clients feel in every session." className="mb-14" />
          <WhyGrid />
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-24 md:py-32" id="process">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading center overline="Our Process" title="Five deliberate steps." className="mb-16" />
          <ProcessTimeline />
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-32" id="faq">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading center overline="FAQ" title="Answers, upfront." className="mb-14" />
          <FAQAccordion />
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="orb h-[300px] w-[300px] bg-blue-600/20 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              Ready to grow smarter?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg text-slate-400">
              Book a consultation and let&rsquo;s build a clear, realistic plan for your digital presence.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-4 sm:flex-row">
              <button
                onClick={openModal}
                className="flex items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-4 font-medium text-white shadow-[0_0_24px_rgba(37,99,235,0.45)] transition-all duration-300 hover:bg-blue-700 hover:-translate-y-1"
                data-testid="cta-book-button"
              >
                Book Consultation <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 font-medium text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
                data-testid="cta-whatsapp-button"
              >
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
